import { Form } from 'antd';
import { DarwinCol, DarwinFlex } from 'components/darwin-ui';
import { DarwinModal } from 'components/darwin-ui/Modal';
import { ErrorType, InputType } from 'types/genericTypes';
import { useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import styles from './index.module.css';
import { useFloatNotification } from 'contexts/FloatNotificationContext';
import { useEffect, useState } from 'react';
import DarwinTypography from 'components/darwin-ui/Typography';
import { GenericInput, GenericInputProps } from 'components/ui/GenericInput';
import { CreateControlDocumentPayload } from 'types/controlDocumentsTypes';

export type FormFields = {
  name: string;
  description: string;
};

const FIELD_DESCRIPTION_MAX_LENGTH = 280;
const TEXT_FIELD_MAX_LENGTH = 50;

interface FieldsModalProps {
  initialField?: CreateControlDocumentPayload;
  type: 'add' | 'edit';
  open: boolean;
  closeModal: () => void;
  onSubmit?: (data: FormFields) => Promise<any>;
}

const defaultValuesForm = {
  name: '',
  description: '',
};

export const CreateOrEditControlDocumentModal = ({
  initialField,
  type = 'add',
  open,
  closeModal,
  onSubmit,
}: FieldsModalProps) => {
  const { t } = useTranslation();
  const { openNotification } = useFloatNotification();
  const [initialValuesOnEditForm, setInitialValuesOnEditForm] = useState({});

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormFields>({
    defaultValues: {
      ...defaultValuesForm,
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (initialField) {
      const payload: FormFields = {
        name: initialField?.name || '',
        description: initialField?.description || '',
      };
      reset(payload);
      setInitialValuesOnEditForm(payload);
    } else {
      reset(defaultValuesForm);
    }
  }, [initialField, open, reset]);

  // Logic to disable button
  const currentValues = useWatch({ control });

  const isEditForm =
    JSON.stringify(currentValues) === JSON.stringify(initialValuesOnEditForm);

  const isNewForm =
    JSON.stringify(currentValues) === JSON.stringify(defaultValuesForm);

  const disableButton = isEditForm || isNewForm;

  const onFormSubmit = async (data: FormFields) => {
    try {
      await onSubmit?.(data);

      openNotification(
        t('success'),
        t('control-documents.submit-modal-success', {
          action:
            type === 'add'
              ? t('control-documents.submit-modal-added')
              : t('control-documents.submit-modal-edited'),
        }),
        'success'
      );
    } catch (err) {
      const apiError = err as ErrorType;
      if (apiError.body?.errorCode) {
        openNotification(t('error'), t(apiError.body.errorCode), 'error');
      } else {
        openNotification(
          t('error'),
          t('control-documents.submit-modal-error', {
            action:
              type === 'add'
                ? t('control-documents.submit-modal-add')
                : t('control-documents.submit-modal-edit'),
          }),
          'error'
        );
      }
    } finally {
      closeModal();
    }
  };

  const onCancel = () => {
    reset();
    closeModal();
  };

  const dataTableInputs: Omit<
    GenericInputProps<FormFields>,
    'control' | 'errors'
  >[] = [
    {
      inputType: InputType.INPUT,
      fieldName: 'name',
      label: t('control-documents.submit-modal-name-field'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t('control-documents.submit-modal-name-field-required'),
      },
      maxLength: TEXT_FIELD_MAX_LENGTH,
    },
    {
      inputType: InputType.TEXTAREA,
      fieldName: 'description',
      label: t('control-documents.submit-modal-description-field'),
      showRequiredAsterisk: true,
      inputRules: {
        required: t(
          'control-documents.submit-modal-description-field-required'
        ),
      },
      maxLength: FIELD_DESCRIPTION_MAX_LENGTH,
    },
  ];

  return (
    <DarwinModal
      open={open}
      title={
        <DarwinTypography.Text className={styles.modalTitle}>
          {type === 'add'
            ? t('control-documents.submit-modal-add-document')
            : t('control-documents.submit-modal-edit-document')}
        </DarwinTypography.Text>
      }
      cancelText={t('cancel')}
      okText={t('save')}
      cancelButtonProps={{ className: styles.modalFooterButton }}
      okButtonProps={{
        disabled: !!disableButton,
        className: styles.modalFooterButton,
      }}
      onCancel={onCancel}
      onOk={handleSubmit(onFormSubmit)}
      footer={(_, { OkBtn, CancelBtn }) => (
        <DarwinFlex
          justify="center"
          align="center"
          className={styles.modalFooter}
        >
          <CancelBtn />
          <OkBtn />
        </DarwinFlex>
      )}
      centered
    >
      <Form layout="vertical" onSubmitCapture={handleSubmit(onFormSubmit)}>
        <DarwinCol>
          {dataTableInputs.map(inputProps => (
            <GenericInput<FormFields>
              key={inputProps.fieldName}
              control={control}
              errors={errors}
              {...inputProps}
            />
          ))}
        </DarwinCol>
      </Form>
    </DarwinModal>
  );
};
